'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Search, Briefcase, UserSearch, Zap } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { cn } from '@/lib/utils';

interface SearchResult {
  id: string;
  name: string;
  subtitle: string;
  type: 'partner' | 'prospect' | 'action';
  url: string;
}

const QUICK_ACTIONS: SearchResult[] = [
  { id: 'action-partners', name: 'Go to Partners', subtitle: 'View all partners', type: 'action', url: '/admin/partners' },
  { id: 'action-prospects', name: 'Go to Prospects', subtitle: 'View all prospects', type: 'action', url: '/admin/prospects' },
  { id: 'action-dashboard', name: 'Go to Dashboard', subtitle: 'Admin overview', type: 'action', url: '/admin' },
];

const TYPE_ICONS = {
  partner: Briefcase,
  prospect: UserSearch,
  action: Zap,
} as const;

const TYPE_LABELS = {
  partner: 'Partner',
  prospect: 'Prospect',
  action: 'Action',
} as const;

export function AdminCommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setResults([]);
      setSelectedIndex(0);
      // Small delay for the dialog animation
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search function
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const searchResults: SearchResult[] = [];

    try {
      // Search advisor_profiles (partners)
      const likeTerm = `%${term}%`;
      const { data: partners, error: partnersError } = await supabase
        .from('advisor_profiles')
        .select('id, full_name, firm_name, referral_code, email')
        .not('referral_code', 'is', null)
        .or(`full_name.ilike.${likeTerm},firm_name.ilike.${likeTerm},referral_code.ilike.${likeTerm}`)
        .limit(5);

      if (partnersError) {
        trackSupabaseError('select', 'advisor_profiles', partnersError);
      } else if (partners) {
        for (const p of partners) {
          searchResults.push({
            id: `partner-${p.id}`,
            name: p.full_name || 'Unknown Partner',
            subtitle: p.firm_name || p.email || p.referral_code || '',
            type: 'partner',
            url: `/admin/partners/${p.id}`,
          });
        }
      }

      // Search partner_prospects
      const prospectsTable = (supabase as unknown as Record<string, unknown> & { from: (table: string) => ReturnType<typeof supabase.from> }).from('partner_prospects');
      const { data: prospects, error: prospectsError } = await prospectsTable
        .select('id, first_name, last_name, company_name, email')
        .or(`first_name.ilike.${likeTerm},last_name.ilike.${likeTerm},company_name.ilike.${likeTerm},email.ilike.${likeTerm}`)
        .limit(5);

      if (prospectsError) {
        trackSupabaseError('select', 'partner_prospects', prospectsError);
      } else if (prospects) {
        for (const p of prospects as Array<{ id: string; first_name: string | null; last_name: string | null; company_name: string | null; email: string | null }>) {
          const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown Prospect';
          searchResults.push({
            id: `prospect-${p.id}`,
            name,
            subtitle: p.company_name || p.email || '',
            type: 'prospect',
            url: `/admin/prospects?search=${encodeURIComponent(name)}`,
          });
        }
      }
    } catch {
      // Silently handle network errors
    }

    setResults(searchResults);
    setSelectedIndex(0);
    setLoading(false);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  // Build display list: search results + quick actions
  const allItems = [...results, ...QUICK_ACTIONS];

  // Navigate to selected result
  const selectItem = useCallback((item: SearchResult) => {
    setOpen(false);
    navigate(item.url);
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allItems[selectedIndex];
      if (item) {
        selectItem(item);
      }
    }
  }, [allItems, selectedIndex, selectItem]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" onKeyDown={handleKeyDown}>
        {/* Search input */}
        <div className="flex items-center border-b px-4">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search partners, prospects, or type a command..."
            className="flex-1 h-12 px-3 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-slate-100 px-1.5 font-mono text-[10px] font-medium text-slate-500">
            ESC
          </kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              Searching...
            </div>
          )}

          {!loading && searchTerm.trim() && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              No results found for "{searchTerm}"
            </div>
          )}

          {/* Search results */}
          {results.length > 0 && (
            <div className="mb-2">
              <div className="px-4 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Results
              </div>
              {results.map((item, index) => {
                const Icon = TYPE_ICONS[item.type];
                return (
                  <button
                    key={item.id}
                    data-index={index}
                    onClick={() => selectItem(item)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                      selectedIndex === index
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-700 hover:bg-slate-50'
                    )}
                  >
                    <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      {item.subtitle && (
                        <div className="text-xs text-slate-400 truncate">{item.subtitle}</div>
                      )}
                    </div>
                    <span className={cn(
                      'text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded',
                      item.type === 'partner' && 'bg-blue-50 text-blue-600',
                      item.type === 'prospect' && 'bg-amber-50 text-amber-600',
                    )}>
                      {TYPE_LABELS[item.type]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Quick Actions */}
          <div>
            <div className="px-4 py-1.5 text-xs font-medium text-slate-400 uppercase tracking-wider">
              Quick Actions
            </div>
            {QUICK_ACTIONS.map((item, i) => {
              const index = results.length + i;
              const Icon = TYPE_ICONS[item.type];
              return (
                <button
                  key={item.id}
                  data-index={index}
                  onClick={() => selectItem(item)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                    selectedIndex === index
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-700 hover:bg-slate-50'
                  )}
                >
                  <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-slate-400">{item.subtitle}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t px-4 py-2 flex items-center gap-4 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <kbd className="inline-flex h-4 items-center rounded border bg-slate-100 px-1 font-mono text-[10px]">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex h-4 items-center rounded border bg-slate-100 px-1 font-mono text-[10px]">↵</kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex h-4 items-center rounded border bg-slate-100 px-1 font-mono text-[10px]">esc</kbd>
            Close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AdminCommandPalette;
